import { useEffect, useState } from "react";

/**
 * Real network connectivity state, sourced from `navigator.onLine` plus
 * window online/offline events. Updated whenever the OS reports a state
 * change.
 *
 * Note: `navigator.onLine` returns true if there is *any* network interface
 * up — it does NOT guarantee internet reachability (think captive portal).
 * For festival CP use, the false-positive "online but blocked by portal"
 * scenario is documented in the runbook; we cannot detect it client-side
 * without making a probe request, which adds traffic and battery cost.
 *
 * Returns `true` when online, `false` when offline.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    function up() {
      setOnline(true);
    }
    function down() {
      setOnline(false);
    }
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    // Sync once on mount in case state diverged before listeners attached
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return online;
}
